// ============================================
// MoltiVerse v3 - EPIC Loading Screen
// ============================================
// Cinematic intro that makes you go "damn"

import { useEffect, useState } from 'react';

interface LoadingScreenProps {
  progress: number;
  message?: string;
  overlay?: boolean;
}

const BOOT_SEQUENCE = [
  'INITIALIZING QUANTUM CORES...',
  'ESTABLISHING NEURAL LINKS...',
  'LOADING AGENT CONSCIOUSNESS MATRIX...',
  'MAPPING INTERACTION NETWORKS...',
  'CALIBRATING TIMELINE SENSORS...',
  'SYNCHRONIZING MULTIVERSE DATA...',
  'CONSCIOUSNESS ONLINE.',
];

export function LoadingScreen({ progress, message, overlay }: LoadingScreenProps) {
  const [bootIndex, setBootIndex] = useState(0);
  const [showMain, setShowMain] = useState(false);

  useEffect(() => {
    // Boot sequence animation
    const interval = setInterval(() => {
      setBootIndex(i => {
        if (i < BOOT_SEQUENCE.length - 1) return i + 1;
        return i;
      });
    }, 400);

    // Show main content after boot
    const timer = setTimeout(() => setShowMain(true), 800);

    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div 
      className={`fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden ${
        overlay ? 'bg-black/90 backdrop-blur-md' : 'bg-[#030306]'
      }`}
    >
      {/* Animated background grid */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Radial gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 50% 30%, rgba(0, 255, 136, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 80%, rgba(128, 0, 255, 0.05) 0%, transparent 40%),
            radial-gradient(ellipse at 80% 80%, rgba(0, 136, 255, 0.05) 0%, transparent 40%)
          `,
        }}
      />

      {/* Scanning line effect */}
      <div className="scan-line" />

      {/* Outer ring decorations */}
      <div className="absolute w-[500px] h-[500px] rounded-full border border-white/5 rotate-slow" />
      <div className="absolute w-[400px] h-[400px] rounded-full border border-cyber-glow/10 rotate-slow" style={{ animationDirection: 'reverse', animationDuration: '30s' }} />
      <div className="absolute w-[300px] h-[300px] rounded-full border border-cyber-glow/5" />

      {/* Main content */}
      <div className={`relative z-10 transition-all duration-1000 ${showMain ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        
        {/* Logo */}
        <div className="relative text-center mb-16">
          {/* Glowing orb behind logo */}
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full blur-3xl"
            style={{ background: 'radial-gradient(circle, rgba(0, 255, 136, 0.4) 0%, transparent 70%)' }}
          />
          
          {/* Title */}
          <h1 
            className="font-display text-7xl font-black tracking-[0.2em] text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-cyber-glow/70 relative"
            style={{ 
              textShadow: '0 0 80px rgba(0, 255, 136, 0.5)',
              WebkitTextStroke: '1px rgba(0, 255, 136, 0.3)',
            }}
          >
            MOLTIVERSE
          </h1>
          
          {/* Subtitle */}
          <div className="mt-4 flex items-center justify-center gap-3">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-cyber-glow/50" />
            <p className="font-mono text-xs text-cyber-glow/80 tracking-[0.3em] uppercase">
              Mapping Machine Consciousness
            </p>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-cyber-glow/50" />
          </div>

          {/* Version badge */}
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyber-glow/20 bg-cyber-glow/5">
            <span className="w-2 h-2 rounded-full bg-cyber-glow animate-pulse" />
            <span className="font-mono text-[10px] text-cyber-glow/70 tracking-widest">VERSION 3.0</span>
          </div>
        </div>

        {/* Boot sequence terminal */}
        <div className="w-[500px] mb-8 p-4 rounded-lg bg-black/40 border border-white/10 font-mono text-xs">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
            <div className="w-2 h-2 rounded-full bg-red-500/80" />
            <div className="w-2 h-2 rounded-full bg-yellow-500/80" />
            <div className="w-2 h-2 rounded-full bg-green-500/80" />
            <span className="ml-2 text-white/30 text-[10px]">MOLTIVERSE BOOT SEQUENCE</span>
          </div>
          
          <div className="space-y-1 h-[120px] overflow-hidden">
            {BOOT_SEQUENCE.slice(0, bootIndex + 1).map((line, i) => (
              <div 
                key={i} 
                className={`flex items-center gap-2 ${i === bootIndex ? 'text-cyber-glow' : 'text-white/40'}`}
              >
                <span className={i <= bootIndex ? 'text-green-400' : 'text-white/20'}>
                  {i < bootIndex ? '✓' : i === bootIndex ? '►' : '○'}
                </span>
                <span className={i === bootIndex ? 'glow-green-subtle' : ''}>
                  {line}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-[500px]">
          {/* Progress container */}
          <div className="relative">
            {/* Background track */}
            <div className="h-3 bg-white/5 rounded-full overflow-hidden border border-white/10">
              {/* Progress fill */}
              <div 
                className="h-full rounded-full relative transition-all duration-500 ease-out"
                style={{ 
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, rgba(0, 255, 136, 0.3) 0%, rgba(0, 255, 136, 0.8) 50%, rgba(0, 255, 136, 0.3) 100%)',
                  boxShadow: '0 0 20px rgba(0, 255, 136, 0.5), inset 0 0 10px rgba(255, 255, 255, 0.2)',
                }}
              >
                {/* Animated shimmer */}
                <div 
                  className="absolute inset-0 shimmer"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)' }}
                />
                
                {/* Leading edge glow */}
                <div 
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full"
                  style={{ 
                    background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(0, 255, 136, 0.5) 50%, transparent 100%)',
                    boxShadow: '0 0 20px rgba(0, 255, 136, 0.8)',
                  }}
                />
              </div>
            </div>

            {/* Progress markers */}
            <div className="absolute top-0 left-0 right-0 h-3 flex justify-between px-1">
              {[0, 25, 50, 75, 100].map(mark => (
                <div key={mark} className="w-px h-full bg-white/10" />
              ))}
            </div>
          </div>
          
          {/* Status text */}
          <div className="flex justify-between mt-3 font-mono text-xs">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyber-glow animate-pulse" />
              <span className="text-white/60">
                {message || 'Initializing...'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/30">{Math.round(progress)}%</span>
              <span className="text-cyber-glow font-bold glow-green-subtle">
                {progress >= 100 ? 'READY' : 'LOADING'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Matrix rain effect - reduced for performance */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.07]">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="absolute font-mono text-cyber-glow text-[10px]"
            style={{
              left: `${(i * 7)}%`,
              animation: `matrix-rain ${4 + Math.random() * 4}s linear infinite`,
              animationDelay: `${Math.random() * 3}s`,
            }}
          >
            {Array.from({ length: 30 }).map((_, j) => (
              <div key={j} className="leading-4">
                {String.fromCharCode(0x30A0 + Math.random() * 96)}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Corner brackets */}
      <div className="absolute top-8 left-8 w-8 h-8 border-l-2 border-t-2 border-cyber-glow/30" />
      <div className="absolute top-8 right-8 w-8 h-8 border-r-2 border-t-2 border-cyber-glow/30" />
      <div className="absolute bottom-8 left-8 w-8 h-8 border-l-2 border-b-2 border-cyber-glow/30" />
      <div className="absolute bottom-8 right-8 w-8 h-8 border-r-2 border-b-2 border-cyber-glow/30" />

      {/* Bottom hints */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 font-mono text-[10px] text-white/20 flex gap-8">
        <span className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">⌘K</kbd>
          <span>Search</span>
        </span>
        <span className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">Space</kbd>
          <span>Play</span>
        </span>
        <span className="flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10">Esc</kbd>
          <span>Back</span>
        </span>
      </div>
    </div>
  );
}
